import { useState, useEffect, useMemo, useCallback } from 'react';
import { notifications } from '@mantine/notifications';
import DashBoardApi from '../../api/DashBoardApi';
import { DashboardProfile } from './dashboardTypes';

export function useDashboardProfiles(selectedWebsite: string) {
  const [profiles, setProfiles] = useState<DashboardProfile[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const fetchProfiles = useCallback(async (preserveActiveId = false) => {
    if (!selectedWebsite) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await DashBoardApi.getDashboardProfiles({
        system_website_id: selectedWebsite,
      });
      const data = await response.json();
      if (data?.status === 1) {
        const fetched = data?.data?.profiles || [];
        setProfiles(fetched);
        if (!preserveActiveId) {
          const defaultId = data?.data?.default_dashboard_profile_id || '';
          setActiveId(defaultId || fetched?.[0]?.dashboard_profile_id || '');
        }
      }
    } catch {
      notifications.show({ color: 'red', message: 'Unable to load dashboards.' });
    } finally {
      setLoading(false);
    }
  }, [selectedWebsite]);

  // Clear old data and re-fetch when website changes
  useEffect(() => {
    setProfiles([]);
    setActiveId('');
    fetchProfiles();
  }, [fetchProfiles]);

  const activeProfile = useMemo(() => {
    if (!profiles.length) return null;
    return profiles.find(p => p.dashboard_profile_id === activeId) || profiles[0];
  }, [profiles, activeId]);

  const createProfile = useCallback(async (payload: any) => {
    const response = await DashBoardApi.createDashboardProfile({
      ...payload,
      system_website_id: selectedWebsite,
    });
    const data = await response.json();
    if (data?.status === 1) {
      notifications.show({ color: 'green', message: 'Dashboard created.' });
      await fetchProfiles();
      if (data?.data?.dashboard_profile_id) {
        setActiveId(data.data.dashboard_profile_id);
      }
      return data.data;
    }
    throw new Error(data?.message || 'Failed to create dashboard');
  }, [fetchProfiles, selectedWebsite]);

  const updateProfile = useCallback(async (profileId: string, payload: any) => {
    const response = await DashBoardApi.updateDashboardProfile({
      dashboard_profile_id: profileId,
      system_website_id: selectedWebsite,
      ...payload,
    });
    const data = await response.json();
    if (data?.status === 1) {
      await fetchProfiles(true);
      return data.data;
    }
    throw new Error(data?.message || 'Failed to save settings');
  }, [fetchProfiles, selectedWebsite]);

  const deleteProfile = useCallback(async (profileId: string) => {
    const response = await DashBoardApi.deleteDashboardProfile({
      dashboard_profile_id: profileId,
      system_website_id: selectedWebsite,
    });
    const data = await response.json();
    if (data?.status === 1) {
      notifications.show({ color: 'green', message: 'Dashboard deleted.' });
      await fetchProfiles();
    }
  }, [fetchProfiles, selectedWebsite]);

  const setDefault = useCallback(async (profileId: string) => {
    const response = await DashBoardApi.setDefaultDashboardProfile({
      dashboard_profile_id: profileId,
      system_website_id: selectedWebsite,
    });
    const data = await response.json();
    if (data?.status === 1) {
      notifications.show({ color: 'green', message: 'Default dashboard updated.' });
      await fetchProfiles(true);
    }
  }, [fetchProfiles, selectedWebsite]);

  return {
    profiles,
    activeProfile,
    activeId,
    setActiveId,
    loading,
    fetchProfiles,
    createProfile,
    updateProfile,
    deleteProfile,
    setDefault,
  };
}
